import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import MarketingNav from "@/components/MarketingNav";
import Seo from "@/components/Seo";
import { API } from "@/context/AuthContext";
import { BIZ } from "@/data/site";
import { CALC_SERVICES, computeEstimate } from "@/data/calculatorPricing";
import { useVisitorProfile } from "@/hooks/useVisitorProfile";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import {
  Calculator, Plus, Trash2, ArrowRight, Phone, MessageSquare,
  CheckCircle2, Info, Loader2, Check,
} from "lucide-react";

const emptyRow = () => ({ id: crypto.randomUUID(), key: "", quantity: "" });

export default function EstimateCalculatorPage() {
  const [rows, setRows] = useState([emptyRow()]);
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const { hasConsent, setConsent, save, hydrate } = useVisitorProfile();
  const [remember, setRemember] = useState(hasConsent);

  // Pre-fill lead fields from the visitor's saved profile on first mount.
  useEffect(() => {
    hydrate(setLead);
  }, [hydrate]);

  const validEntries = rows
    .filter((r) => r.key && r.quantity)
    .map((r) => ({ key: r.key, quantity: parseFloat(r.quantity) || 0 }))
    .filter((r) => r.quantity > 0);

  const { low, high, lineItems } = useMemo(() => computeEstimate(validEntries), [rows]);

  const addRow = () => setRows((r) => [...r, emptyRow()]);
  const removeRow = (id) => setRows((r) => (r.length === 1 ? r : r.filter((x) => x.id !== id)));
  const updateRow = (id, field, value) =>
    setRows((r) => r.map((row) => (row.id === id ? { ...row, [field]: value } : row)));

  const track = (name, extra = {}) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", name, extra);
    }
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({ event: name, ...extra });
    }
  };

  const revealLead = () => {
    if (validEntries.length === 0) {
      toast.error("Add at least one service with a quantity to see your estimate.");
      return;
    }
    track("calculator_estimate_viewed", { estimate_low: low, estimate_high: high });
    setShowLead(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!lead.name.trim()) return toast.error("Please add your name.");
    if (!lead.phone.trim()) return toast.error("Please add your phone number.");
    if (!lead.email.trim()) return toast.error("Please add your email.");
    if (!lead.address.trim()) return toast.error("Please add your service address.");
    if (lineItems.length === 0) return toast.error("Add at least one service with a quantity.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/estimate-leads`, {
        name: lead.name.trim(),
        phone: lead.phone.trim(),
        email: lead.email.trim(),
        address: lead.address.trim(),
        notes: lead.notes.trim() || undefined,
        services: lineItems,
        estimate_low: low,
        estimate_high: high,
      });
      track("calculator_lead_submitted", { estimate_low: low, estimate_high: high });
      if (remember) {
        setConsent(true);
        save(lead);
      } else {
        setConsent(false);
      }
      setConfirmed(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-paper min-h-screen">
      <Seo
        title="Instant Pressure Washing Estimate Calculator"
        description={`Get a preliminary online estimate for house washing, soft washing, roof cleaning, driveways, gutters, and windows in ${BIZ.city}, ${BIZ.stateShort}. Free, no obligation.`}
        path="/pressure-washing-estimate-calculator/"
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-28 md:pt-32 pb-8 bg-gradient-to-b from-brand-50 via-white to-transparent">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-100 text-brand-700 px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-bold">
            <Calculator className="w-3.5 h-3.5" />
            Instant online estimate
          </div>
          <h1
            className="mt-6 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 leading-[1.05]"
            style={{ fontFamily: "Outfit" }}
          >
            See your price in <span className="text-electric-500 italic">under a minute.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-stone-600 max-w-2xl mx-auto">
            Pick your services, add rough measurements, and we&apos;ll return a preliminary estimate range on the spot. No pressure, no obligation.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start">
          {/* Left: Service rows */}
          <div className="bg-white rounded-3xl p-6 md:p-8 border border-stone-200 shadow-sm">
            <h2 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: "Outfit" }}>
              1. Choose your services
            </h2>
            <p className="text-sm text-stone-500 mb-6">
              Add rows for each surface you want cleaned. Rough numbers are fine — we&apos;ll refine on-site.
            </p>

            <div className="space-y-4" data-testid="calculator-rows">
              {rows.map((row, i) => {
                const svc = CALC_SERVICES.find((s) => s.key === row.key);
                return (
                  <div
                    key={row.id}
                    data-testid={`calculator-row-${i}`}
                    className="grid grid-cols-1 md:grid-cols-[1fr_140px_44px] gap-3"
                  >
                    <select
                      value={row.key}
                      onChange={(e) => updateRow(row.id, "key", e.target.value)}
                      data-testid={`calc-svc-${i}`}
                      className="px-4 py-3 bg-stone-50 border border-stone-200 rounded-lg text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white"
                    >
                      <option value="">— Select a service —</option>
                      {CALC_SERVICES.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.label} (${s.rate}/{s.unit === "sqft" ? "sq ft" : s.unit === "linft" ? "linear ft" : s.unit === "pane" ? "pane" : "each"})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      placeholder={svc ? svc.unitLabel : "Quantity"}
                      value={row.quantity}
                      onChange={(e) => updateRow(row.id, "quantity", e.target.value)}
                      data-testid={`calc-qty-${i}`}
                      className="px-4 py-3 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      disabled={rows.length === 1}
                      className="p-3 rounded-lg bg-stone-100 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label="Remove row"
                    >
                      <Trash2 className="w-4 h-4 mx-auto" />
                    </button>
                    {svc && (
                      <div className="md:col-span-3 text-xs text-stone-500 italic pl-1 -mt-1">
                        {svc.hint} · minimum <b>${svc.minimum}</b>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={addRow}
              data-testid="calc-add-row"
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-brand-700 hover:text-electric-500 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add another service
            </button>

            <div className="mt-8 pt-6 border-t border-stone-100 flex items-start gap-3 text-xs text-stone-500">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-brand-500" />
              <span>
                <b className="text-stone-700">Estimate disclaimer.</b> Calculator results are preliminary
                estimates and are not a binding quote. Final pricing is confirmed after reviewing property
                details, photos, or an on-site evaluation. Height, access, organic growth, obstacles, water
                availability, and requested add-ons can move the final price.
              </span>
            </div>
          </div>

          {/* Right: Sticky estimate */}
          <div
            className="bg-brand-700 rounded-3xl p-6 md:p-8 text-white shadow-2xl border-t-4 border-electric-500 lg:sticky lg:top-32"
            data-testid="calc-estimate-card"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-electric-300">
              Preliminary estimate
            </div>
            <div className="mt-3 text-4xl md:text-5xl font-extrabold" style={{ fontFamily: "Outfit" }} data-testid="calc-estimate-value">
              {low > 0 ? `$${low}–$${high}` : "—"}
            </div>
            <div className="text-sm text-white/70 mt-2">
              {lineItems.length === 0
                ? "Add services on the left to see your range."
                : `${lineItems.length} service${lineItems.length !== 1 ? "s" : ""} · minimums applied`}
            </div>

            {lineItems.length > 0 && (
              <div className="mt-5 pt-5 border-t border-white/10 space-y-2 text-sm">
                {lineItems.map((li, i) => (
                  <div key={i} className="flex justify-between text-white/85">
                    <span className="truncate mr-2">{li.service}</span>
                    <span className="font-mono font-semibold">${Math.round(li.subtotal)}</span>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={revealLead}
              disabled={validEntries.length === 0}
              data-testid="calc-request-quote-btn"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-electric-500 hover:bg-electric-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-6 py-4 rounded-full shadow-lg transition-all hover:-translate-y-0.5"
            >
              Send me my estimate
              <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <a
                href={BIZ.phoneTel && `tel:${BIZ.phoneTel}`}
                onClick={() => track("click_to_call", { source: "calculator" })}
                data-testid="calc-call-btn"
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/30 bg-white/10 hover:bg-white/20 text-white px-3 py-2 font-semibold transition-colors"
              >
                <Phone className="w-3.5 h-3.5" />
                Call
              </a>
              <a
                href={BIZ.phoneSmsHref}
                onClick={() => track("click_to_text", { source: "calculator" })}
                data-testid="calc-text-btn"
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/30 bg-white/10 hover:bg-white/20 text-white px-3 py-2 font-semibold transition-colors"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                Text
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Lead form modal */}
      {showLead && !confirmed && (
        <div
          className="fixed inset-0 z-[60] bg-brand-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4 md:p-8"
          onClick={() => setShowLead(false)}
          data-testid="calc-lead-modal"
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl w-full max-w-lg p-6 md:p-8 max-h-[90vh] overflow-y-auto"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brand-700">2. Send it to us</div>
            <h3 className="mt-2 text-2xl font-extrabold text-stone-900 leading-tight" style={{ fontFamily: "Outfit" }}>
              Your estimate: <span className="text-electric-500">${low}–${high}</span>
            </h3>
            <p className="text-sm text-stone-500 mt-2">
              Drop your details and we&apos;ll confirm your final pricing after a quick review — usually {BIZ.responseTime}.
            </p>
            <div className="mt-6 space-y-4">
              <LeadField label="Your name" value={lead.name} onChange={(v) => setLead({ ...lead, name: v })} testId="lead-name" required autoComplete="name" />
              <LeadField label="Phone" type="tel" value={lead.phone} onChange={(v) => setLead({ ...lead, phone: v })} testId="lead-phone" required autoComplete="tel" />
              <LeadField label="Email" type="email" value={lead.email} onChange={(v) => setLead({ ...lead, email: v })} testId="lead-email" required autoComplete="email" />
              <LeadField label="Service address" value={lead.address} onChange={(v) => setLead({ ...lead, address: v })} testId="lead-address" required autoComplete="street-address" google />
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-1.5 block">Notes (optional)</label>
                <textarea
                  rows={3}
                  value={lead.notes}
                  onChange={(e) => setLead({ ...lead, notes: e.target.value })}
                  placeholder="Access details, gate code, preferred day, photos link…"
                  data-testid="lead-notes"
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-600 hover:bg-electric-500 disabled:opacity-60 text-white font-bold px-6 py-4 rounded-full transition-all hover:-translate-y-0.5 shadow-lg"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {submitting ? "Sending..." : "Request a final quote"}
              </button>

              <label
                data-testid="lead-remember-toggle"
                className="flex items-start gap-3 cursor-pointer group select-none"
              >
                <span
                  aria-hidden="true"
                  className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                    remember
                      ? "bg-brand-600 border-brand-600"
                      : "border-stone-300 group-hover:border-brand-400 bg-white"
                  }`}
                >
                  {remember && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                </span>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="lead-remember-checkbox"
                />
                <span className="text-xs text-stone-700 leading-relaxed">
                  <span className="font-semibold">Remember my info on this device</span>
                  <span className="text-stone-500"> — so you don&apos;t have to retype next time. (Stored only in your browser.)</span>
                </span>
              </label>
              <p className="text-xs text-stone-500 text-center">
                By submitting you agree to be contacted about your project. Standard message rates apply if you also text us.
              </p>
            </div>
          </form>
        </div>
      )}

      {confirmed && (
        <div className="fixed inset-0 z-[60] bg-brand-900/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8">
          <div
            className="bg-white rounded-3xl max-w-md w-full p-8 text-center"
            data-testid="calc-lead-confirmed"
          >
            <div className="w-14 h-14 mx-auto rounded-full bg-electric-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-electric-500" />
            </div>
            <h3
              className="mt-5 text-2xl font-extrabold text-stone-900"
              style={{ fontFamily: "Outfit" }}
            >
              Got it! We&apos;ll be in touch.
            </h3>
            <p className="mt-3 text-sm text-stone-600 leading-relaxed">
              We&apos;ll confirm your final pricing within a few hours — often within an hour, any hour. Watch for a call or text from{" "}
              <b>{BIZ.phone}</b>.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <a
                href={`tel:${BIZ.phoneTel}`}
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-600 hover:bg-brand-700 text-white px-4 py-3 text-sm font-bold"
              >
                <Phone className="w-4 h-4" />
                Call now
              </a>
              <Link
                to="/"
                onClick={() => { setConfirmed(false); setShowLead(false); }}
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-stone-200 text-stone-700 px-4 py-3 text-sm font-bold hover:bg-stone-50"
              >
                Back home
              </Link>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function LeadField({ label, value, onChange, testId, type = "text", required, autoComplete, google }) {
  const inputCls = "w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white";
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-1.5 block">
        {label}{required && <span className="text-brand-700 ml-1">*</span>}
      </span>
      {google ? (
        <GoogleAddressInput
          value={value}
          onChange={onChange}
          testId={testId}
          required={required}
          autoComplete={autoComplete}
          className={inputCls}
          placeholder="Start typing — pick your address"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          data-testid={testId}
          autoComplete={autoComplete}
          className={inputCls}
        />
      )}
    </label>
  );
}

function Footer() {
  return (
    <footer className="bg-stone-900 text-stone-400 py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12 text-center text-xs text-stone-500">
        © {new Date().getFullYear()} {BIZ.name} · {BIZ.city}, {BIZ.stateShort} {BIZ.zip}
      </div>
    </footer>
  );
}
